import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { spellCheckAPI } from '../lib/api';
import { Check, X, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * SpellCheckInput - An input component with AI-powered spell checking
 * 
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Change handler
 * @param {string} props.context - Context hint for spell checker (e.g., "description", "name")
 * @param {boolean} props.multiline - Use textarea instead of input
 * @param {number} props.debounceMs - Debounce delay in ms (default: 800)
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.rest - Other props passed to Input/Textarea
 */
export const SpellCheckInput = ({
  value = '',
  onChange,
  context = '',
  multiline = false,
  debounceMs = 800,
  className,
  disabled,
  ...rest
}) => {
  const [checking, setChecking] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [correctedText, setCorrectedText] = useState('');
  const [hasErrors, setHasErrors] = useState(false);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced spell check
  const checkSpelling = useCallback(async (text) => {
    if (!text || text.trim().length < 3) {
      setSuggestions([]);
      setHasErrors(false);
      return;
    }

    try {
      setChecking(true);
      const response = await spellCheckAPI.check(text, context);
      const data = response.data;
      
      setSuggestions(data.suggestions || []);
      setCorrectedText(data.corrected_text || text);
      setHasErrors(data.has_errors || false);
      
      if (data.has_errors && data.suggestions?.length > 0) {
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Spell check error:', error);
      setSuggestions([]);
      setHasErrors(false);
    } finally {
      setChecking(false);
    }
  }, [context]);

  // Handle input change with debounce
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(e);
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Set new timer for spell check
    debounceTimer.current = setTimeout(() => {
      checkSpelling(newValue);
    }, debounceMs);
  };

  // Apply a single suggestion
  const applySuggestion = (suggestion) => {
    const newText = value.substring(0, suggestion.start_index) + 
                    suggestion.suggestion + 
                    value.substring(suggestion.end_index);
    
    // Create a synthetic event
    const syntheticEvent = {
      target: { value: newText }
    };
    onChange(syntheticEvent);
    
    // Re-check after applying
    setTimeout(() => checkSpelling(newText), 100);
  };

  // Apply all corrections
  const applyAllCorrections = () => {
    const syntheticEvent = {
      target: { value: correctedText }
    };
    onChange(syntheticEvent);
    setSuggestions([]);
    setHasErrors(false);
    setShowSuggestions(false);
  };

  // Dismiss suggestions
  const dismissSuggestions = () => {
    setSuggestions([]);
    setShowSuggestions(false);
    setHasErrors(false);
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="relative">
      <div className="relative">
        <InputComponent
          ref={inputRef}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={cn(
            className,
            hasErrors && suggestions.length > 0 && 'border-amber-400 focus:border-amber-500 pr-20'
          )}
          {...rest}
        />
        
        {/* Status indicators */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {checking && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {!checking && hasErrors && suggestions.length > 0 && (
            <button
              type="button"
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="flex items-center gap-1 text-amber-600 hover:text-amber-700 text-xs font-medium px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 transition-colors"
            >
              <AlertCircle className="h-3 w-3" />
              {suggestions.length} {suggestions.length === 1 ? 'issue' : 'issues'}
            </button>
          )}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 mt-1 w-full bg-white border border-amber-200 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">
              Spelling Suggestions
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={applyAllCorrections}
              >
                <Check className="h-3 w-3 mr-1" />
                Fix All
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-gray-500 hover:text-gray-700"
                onClick={dismissSuggestions}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          <div className="max-h-48 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
              >
                <div className="flex items-center gap-2">
                  <span className="text-red-500 line-through text-sm">
                    {suggestion.original}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-green-600 font-medium text-sm">
                    {suggestion.suggestion}
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-6 text-xs"
                  onClick={() => applySuggestion(suggestion)}
                >
                  Apply
                </Button>
              </div>
            ))}
          </div>
          
          {correctedText && correctedText !== value && (
            <div className="p-2 bg-green-50 border-t border-green-200">
              <p className="text-xs text-green-800 mb-1">Corrected text:</p>
              <p className="text-sm text-green-900 font-medium">{correctedText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpellCheckInput;
